/**
 * PlanApprovalCard
 *
 * In-thread plan-then-approve card. Reuses Confirmation / waiting_approval
 * patterns (same family as ContentEngineApprovalCard) — not a third system.
 *
 * Primary responsibilities:
 * - Render structured propose_plan steps
 * - Approve / Edit / Reject until mutate tools may run
 *
 * Dependencies:
 * - Confirmation AI Elements
 * - Badge / Textarea primitives
 */

'use client'

import { useMemo, useState } from 'react'
import { IconCheck, IconEdit, IconListCheck, IconX } from '@tabler/icons-react'

import { Badge } from '@/components/ui/badge'
import { Textarea } from '@/components/ui/textarea'
import {
  Confirmation,
  ConfirmationAction,
  ConfirmationActions,
  ConfirmationRequest,
  ConfirmationTitle
} from '@/components/ai-elements/confirmation'
import {
  isPlanAwaitingApproval,
  parseProposedPlanSteps,
  PLAN_APPROVAL_CARD_FOOTER,
  PLAN_APPROVAL_CARD_TITLE,
  PLAN_APPROVAL_STEP_MUTATE_BADGE,
  PLAN_APPROVAL_STEP_READ_BADGE,
  type ProposedPlanStep
} from '@/lib/chat/propose-plan'
import { cn } from '@/lib/utils'

export type PlanApprovalCardProps = {
  annotation?: Record<string, unknown> | unknown[] | null
  className?: string
  content?: string | null
  disabled?: boolean
  onApprove: (content: string) => Promise<void> | void
  onReject: () => Promise<void> | void
  status?: string | null
  steps?: unknown
}

function readPlanFromAnnotation(
  annotation: Record<string, unknown> | unknown[] | null | undefined
) {
  if (!annotation) return null

  // Persisted annotations are often an array of partial objects.
  if (Array.isArray(annotation)) {
    for (const entry of annotation) {
      if (!entry || typeof entry !== 'object' || Array.isArray(entry)) continue
      const found = readPlanFromAnnotation(entry as Record<string, unknown>)
      if (found) return found
    }
    return null
  }

  const raw =
    annotation.propose_plan ||
    annotation.show_plan ||
    annotation.plan_approval

  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return null
  }

  const record = raw as Record<string, unknown>
  return {
    planId: typeof record.planId === 'string' ? record.planId : null,
    content: typeof record.content === 'string' ? record.content : '',
    status: typeof record.status === 'string' ? record.status : null,
    steps: record.steps
  }
}

export function PlanApprovalCard({
  annotation,
  className,
  content: contentProp,
  disabled = false,
  onApprove,
  onReject,
  status: statusProp,
  steps: stepsProp
}: PlanApprovalCardProps) {
  const fromAnnotation = useMemo(
    () => readPlanFromAnnotation(annotation ?? null),
    [annotation]
  )

  const [editing, setEditing] = useState(false)
  const [editContent, setEditContent] = useState(
    contentProp || fromAnnotation?.content || ''
  )
  const [busy, setBusy] = useState<'approve' | 'reject' | null>(null)
  const [localStatus, setLocalStatus] = useState<string | null>(null)

  const status = localStatus || statusProp || fromAnnotation?.status || null
  const initialContent = contentProp || fromAnnotation?.content || ''
  const steps: ProposedPlanStep[] = useMemo(
    () => parseProposedPlanSteps(stepsProp ?? fromAnnotation?.steps),
    [fromAnnotation?.steps, stepsProp]
  )

  if (!isPlanAwaitingApproval(status)) {
    return null
  }

  const run = async (decision: 'approve' | 'reject') => {
    if (busy || disabled) return
    setBusy(decision)
    try {
      if (decision === 'approve') {
        await onApprove(editContent.trim() || initialContent)
        setLocalStatus('active')
      } else {
        await onReject()
        setLocalStatus('rejected')
      }
    } finally {
      setBusy(null)
    }
  }

  return (
    <Confirmation
      approval={{ id: 'propose-plan', approved: null }}
      state="approval-requested"
      className={cn('not-prose my-3', className)}
    >
      <div className="flex flex-wrap items-center gap-2">
        <IconListCheck className="text-muted-foreground size-4" />
        <ConfirmationTitle>
          {PLAN_APPROVAL_CARD_TITLE}
        </ConfirmationTitle>
      </div>

      <ConfirmationRequest className="mt-2 space-y-2">
        {editing ? (
          <Textarea
            value={editContent}
            onChange={event => setEditContent(event.target.value)}
            rows={8}
            className="font-mono text-xs"
            disabled={disabled || busy !== null}
          />
        ) : steps.length > 0 ? (
          <ol className="list-decimal space-y-1.5 pl-4 text-xs">
            {steps.map(step => (
              <li key={step.id}>
                <span className="font-medium">{step.title}</span>
                <Badge
                  variant={step.kind === 'mutate' ? 'destructive' : 'secondary'}
                  className="ml-2 text-[10px]"
                >
                  {step.kind === 'mutate'
                    ? PLAN_APPROVAL_STEP_MUTATE_BADGE
                    : PLAN_APPROVAL_STEP_READ_BADGE}
                </Badge>
                {step.detail?.trim() ? (
                  <div className="text-muted-foreground mt-0.5">{step.detail}</div>
                ) : null}
              </li>
            ))}
          </ol>
        ) : (
          <pre className="bg-muted/40 max-h-48 overflow-auto whitespace-pre-wrap rounded-md p-2 text-xs">
            {initialContent}
          </pre>
        )}
        <p
          className="text-muted-foreground text-[11px]"
          role="status"
          aria-live="polite"
          aria-atomic="true"
        >
          {PLAN_APPROVAL_CARD_FOOTER}
        </p>
      </ConfirmationRequest>

      <ConfirmationActions>
        <ConfirmationAction
          disabled={disabled || busy !== null}
          onClick={() => {
            void run('approve')
          }}
        >
          <IconCheck className="mr-1 size-3.5" />
          {busy === 'approve' ? 'Approving…' : 'Approve'}
        </ConfirmationAction>
        <ConfirmationAction
          variant="secondary"
          disabled={disabled || busy !== null}
          onClick={() => {
            if (!editing) {
              setEditContent(initialContent)
            }
            setEditing(current => !current)
          }}
        >
          <IconEdit className="mr-1 size-3.5" />
          {editing ? 'Done editing' : 'Edit'}
        </ConfirmationAction>
        <ConfirmationAction
          variant="ghost"
          disabled={disabled || busy !== null}
          onClick={() => {
            void run('reject')
          }}
        >
          <IconX className="mr-1 size-3.5" />
          {busy === 'reject' ? 'Rejecting…' : 'Reject'}
        </ConfirmationAction>
      </ConfirmationActions>
    </Confirmation>
  )
}
